import { useEffect, useMemo, useRef } from 'react';
import type { CSSObject } from '@emotion/react';
import { AnimatePresence, motion, MotionProps } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { theme } from '@frontend/theme';
import { Button, useOnClickOutside } from '@frontend/design-system';
import { ThreadDrawerProps } from './thread-drawer';

export type ThreadPopoverDrawerProps = Omit<ThreadDrawerProps, 'closeOnClickOutside' | 'padding'> & {
  containerMaxHeight?: string | number;
  size?: 'normal' | 'small';
  shiftedFromTop?: number;
};

const closeButtonContainerStyles: CSSObject = {
  position: 'sticky',
  bottom: 0,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'var(--fabric-number-n-2-px)',
};

const closeButtonStyles: CSSObject = {
  fontWeight: 'var(--fabric-font-weight-normal)',
  fontSize: 'var(--fabric-font-size-sm)',
};

export const ThreadPopoverDrawer = ({
  open,
  onClose,
  children,
  keepMounted,
  closeLabel,
  hideCloseButton,
  closeButtonRef,
  layout = 'size',
  onKeyDown,
  containerMaxHeight = 'none',
  size = 'normal',
  shiftedFromTop = 0,
  ...rest
}: MotionProps & ThreadPopoverDrawerProps) => {
  const { t } = useTranslation('thread-header');
  const focusRef = useRef<HTMLDivElement>(null);
  const { show: isSlidePanelOpen } = useSlidePanelShallowStore('show');

  useOnClickOutside({
    active: open && !isSlidePanelOpen,
    ref: focusRef,
    handler: onClose,
    ignoreRefs: closeButtonRef ? [closeButtonRef] : undefined,
  });

  useEffect(() => {
    if (open) {
      focusRef.current?.focus();
    }
  }, [open]);

  const mount = useMemo(() => open || keepMounted, [open, keepMounted]);

  return (
    <AnimatePresence>
      {mount && (
        <motion.div
          layout={layout}
          initial={{
            height: 0,
            opacity: 0,
          }}
          animate={{
            height: open ? 'auto' : 0,
            opacity: open ? 1 : 0,
          }}
          exit={{
            height: 0,
            opacity: 0,
          }}
          css={{
            position: 'absolute',
            top: shiftedFromTop,
            left: 0,
            right: 0,
            overflow: 'hidden',
            maxHeight: containerMaxHeight,
            zIndex: theme.zIndex.popover,
          }}
        >
          <motion.div
            css={{
              backgroundImage: `linear-gradient(to top, ${theme.colors.primary90}00, ${theme.colors.primary90}4D)`,
              padding: size === 'normal' ? theme.spacing(2, 2, 12) : theme.spacing(1, 1, 8),
              overflow: 'hidden',
              height: '100%',
              width: '100%',
            }}
            onKeyDown={(e) => {
              if (open && e.key === 'Escape') {
                onClose();
              }
              onKeyDown?.(e);
            }}
            transition={{
              top: {
                type: false,
              },
              left: {
                type: false,
              },
              right: {
                type: false,
              },
            }}
            {...rest}
          >
            <motion.div
              ref={focusRef}
              animate={
                open
                  ? {
                      height: '100%',
                      boxShadow: theme.shadows.floating,
                      opacity: 1,
                    }
                  : {
                      height: 0,
                      boxShadow: 'none',
                      opacity: 0,
                    }
              }
              exit={{
                height: 0,
                boxShadow: 'none',
                opacity: 0,
              }}
              css={{
                backgroundColor: theme.colors.white,
                borderRadius: theme.borderRadius.medium,
                overflow: 'hidden',
              }}
            >
              {children}
              {!hideCloseButton && (
                <div css={closeButtonContainerStyles}>
                  <Button variant='tertiary' css={closeButtonStyles} onClick={onClose}>
                    {closeLabel || t('Hide')}
                  </Button>
                </div>
              )}
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
